import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import MedicationIcon from './MedicationIcon';

const MedicationSearchInput = ({ 
  value = '',
  onChange,
  onTemplateSelect,
  medicationTemplates = [],
  commonMedications = [],
  showCommonMedications = true,
  medicationType = null,
  placeholder = "Type medication name...",
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Sync searchTerm with external value
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build medication options from templates and common medications
  const medicationOptions = useMemo(() => {
    const options = [];
    
    // Add personal medication templates
    if (medicationTemplates.length > 0) {
      medicationTemplates.forEach(m => {
        options.push({
          id: m.id,
          label: m.medication_name,
          type: m.medication_type ? m.medication_type.toLowerCase() : null,
          isTemplate: true,
          data: m
        });
      });
    }
    
    // Add common medications if enabled
    if (showCommonMedications && commonMedications.length > 0) {
      commonMedications.forEach(m => {
        options.push({
          id: `common-${m.id}`,
          label: m.medication_name,
          type: m.medication_type ? m.medication_type.toLowerCase() : null,
          isCommon: true,
          data: m
        });
      });
    }
    
    return options;
  }, [medicationTemplates, commonMedications, showCommonMedications]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    const searchLower = searchTerm.toLowerCase();
    return medicationOptions.filter(option => 
      option.label.toLowerCase().includes(searchLower)
    );
  }, [medicationOptions, searchTerm]);

  // Limit displayed options
  const displayedOptions = useMemo(() => {
    return filteredOptions.slice(0, 30);
  }, [filteredOptions]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    
    // Show dropdown when typing (if there are 2+ characters)
    if (newValue.length >= 2) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleSelect = (option) => {
    setSearchTerm(option.label);
    onChange(option.label);
    onTemplateSelect(option.id, option.data, option.isTemplate, option.isCommon);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm('');
    onChange('');
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    if (searchTerm.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    // Close dropdown on Escape
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input Field with Icon */}
      <div className="relative flex items-center">
        {medicationType && (
          <div className="absolute left-2 z-10">
            <MedicationIcon 
              type={medicationType} 
              size={16} 
              className="text-gray-600" 
            />
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
            medicationType ? 'pl-8' : ''
          } ${searchTerm ? 'pr-8' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        {searchTerm && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown Suggestions */}
      {isOpen && displayedOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {displayedOptions.map((option, index) => (
            <button
              key={option.id || index}
              type="button"
              onClick={() => handleSelect(option)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
            >
              {option.type && (
                <MedicationIcon 
                  type={option.type} 
                  size={16} 
                  className="text-gray-600 flex-shrink-0" 
                />
              )}
              <span className="flex-1 truncate">{option.label}</span>
              {option.isTemplate && (
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded flex-shrink-0">Personal</span>
              )}
              {option.isCommon && (
                <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded flex-shrink-0">Common</span>
              )}
            </button>
          ))}
          {filteredOptions.length > 30 && (
            <div className="px-3 py-2 text-center text-xs text-gray-400 border-t bg-gray-50">
              Showing 30 of {filteredOptions.length} results. Keep typing to refine.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MedicationSearchInput;
